import { IMAGE_MODELS, type ImageModelId } from '@paint-ai/shared';
import { Cpu, Sparkles, Zap } from 'lucide-react';
import type { ReactNode } from 'react';
import { Badge, Card } from '@/components/ui';
import { cn } from '@/lib/cn';

// Presentation only; ids, names and pricing come from the shared model catalog.
const PRESENTATION: Partial<
  Record<ImageModelId, { badge: string; tone: 'blue' | 'purple'; description: string; icon: ReactNode }>
> = {
  'gemini-2.5-flash-image': {
    badge: 'Standard',
    tone: 'blue',
    description: 'Fast image-to-image editing model for architectural renders',
    icon: <Zap className="w-4 h-4 text-blue-600" />,
  },
  'gemini-3.1-flash-image': {
    badge: 'High-Res (3.1)',
    tone: 'purple',
    description: 'Gemini 3.1 Flash model for high-fidelity architectural renders',
    icon: <Sparkles className="w-4 h-4 text-purple-600" />,
  },
};

const SELECTABLE = IMAGE_MODELS.filter((model) => model.selectable);

interface ModelSelectorProps {
  value: ImageModelId;
  onChange: (model: ImageModelId) => void;
}

export function ModelSelector({ value, onChange }: ModelSelectorProps) {
  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
          <Cpu className="w-3.5 h-3.5 text-blue-600" />
          AI Generation Model
        </h2>
        <span className="text-[10px] font-semibold text-slate-500">Image-to-Image Engine</span>
      </div>

      <div role="radiogroup" aria-label="AI generation model" className="grid grid-cols-1 gap-2">
        {SELECTABLE.map((model) => {
          const selected = value === model.id;
          const ui = PRESENTATION[model.id];
          return (
            <label
              key={model.id}
              className={cn(
                'flex items-start gap-3 p-3 rounded-xl border transition-all cursor-pointer',
                selected
                  ? 'bg-blue-50/80 border-blue-500 shadow-xs ring-1 ring-blue-500/30'
                  : 'bg-slate-50/60 border-slate-200/80 hover:bg-slate-50 hover:border-slate-300',
              )}
            >
              <input
                type="radio"
                name="gemini-model"
                value={model.id}
                checked={selected}
                onChange={() => onChange(model.id)}
                className="mt-1 w-4 h-4 text-blue-600 focus:ring-blue-500 border-slate-300"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-mono font-bold text-slate-900 flex items-center gap-1.5">
                    {ui?.icon}
                    {model.id}
                  </span>
                  <Badge tone={ui?.tone ?? 'slate'} className="text-[9px] px-2">
                    {ui?.badge ?? model.tier}
                  </Badge>
                </div>
                <p className="text-[11px] text-slate-500 font-medium mt-0.5">{ui?.description ?? model.displayName}</p>
              </div>
            </label>
          );
        })}
      </div>
    </Card>
  );
}
